import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import axios from 'axios';
import '@/App.css';
import '@/index.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import NicknameModal from './components/NicknameModal';
import HomePage from './pages/HomePageNew';
import NewsPage from './pages/NewsPage';
import ArticlePage from './pages/ArticlePage';
import FanZonePage from './pages/FanZonePage';
import PollsPage from './pages/PollsPage';
import QBClubPage from './pages/QBClubPage';
import QBAnalyticsPage from './pages/QBAnalyticsPage';
import JacksonStatePage from './pages/JacksonStatePage';
import SWACPickemPage from './pages/SWACPickemPage';
import AdminPickemPage from './pages/AdminPickemPage';
import MembersPage from './pages/MembersPage';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import JacksonStateSoccerPage from './pages/JacksonStateSoccerPage';
import BoomBoxClassicPage from './pages/BoomBoxClassicPage';
import SWACScoreboardPage from './pages/SWACScoreboardPage';
import PickHistoryPage from './pages/PickHistoryPage';
import GamedayScoreboardPage from './pages/GamedayScoreboardPage';
import JSUBasketballArticlePage from './pages/JSUBasketballArticlePage';
import JSUvsAAMUArticlePage from './pages/JSUvsAAMUArticlePage';
import SWACTVArticlePage from './pages/SWACTVArticlePage';
import InteractiveArticleViewer from './pages/InteractiveArticleViewer';
import NotFoundPage from './pages/NotFoundPage';
import { Toaster } from 'sonner';
import { getVisitorId } from './lib/visitorId';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Check for OAuth error in URL query params
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (error) {
      console.error('OAuth error:', error, errorDescription);
      // Redirect to login with error message
      navigate('/login', { 
        replace: true, 
        state: { error: errorDescription || 'Login failed. Please try again.' }
      });
      return;
    }

    const hash = window.location.hash;
    console.log('AuthCallback: Processing hash:', hash);
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');
    
    // Also check for error in hash params
    const hashError = params.get('error');
    const hashErrorDesc = params.get('error_description');
    
    if (hashError) {
      console.error('OAuth hash error:', hashError, hashErrorDesc);
      navigate('/login', { 
        replace: true, 
        state: { error: hashErrorDesc || 'Login failed. Please try again.' }
      });
      return;
    }
    
    console.log('AuthCallback: Session ID:', sessionId);

    if (sessionId) {
      axios.post(`${API}/auth/session`, null, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true
      })
      .then(response => {
        console.log('AuthCallback: Session created successfully', response.data);
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        
        // Store user in localStorage as backup
        localStorage.setItem('slt_user', JSON.stringify(response.data));
        
        // Check for return URL (stored by SLTGameBridge.login() or other sources)
        // Use localStorage since sessionStorage doesn't persist across external auth redirects
        const returnUrl = localStorage.getItem('authReturnUrl');
        localStorage.removeItem('authReturnUrl');
        
        // Small delay to ensure cookie is set
        setTimeout(() => {
          if (returnUrl && returnUrl !== '/' && returnUrl !== '/login') {
            console.log('AuthCallback: Returning to:', returnUrl);
            navigate(returnUrl, { state: { user: response.data }, replace: true });
          } else {
            navigate('/', { state: { user: response.data }, replace: true });
          }
        }, 100);
      })
      .catch(error => {
        console.error('Auth error:', error);
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/login', { 
          replace: true,
          state: { error: 'Session expired. Please try logging in again.' }
        });
      });
    } else {
      console.log('AuthCallback: No session ID found, redirecting home');
      navigate('/', { replace: true });
    }
  }, [navigate]);

  return <div className="flex items-center justify-center min-h-screen bg-slate-50">
    <div className="text-center">
      <div className="animate-spin h-12 w-12 border-4 border-[#DC2626] border-t-transparent rounded-full mx-auto mb-4"></div>
      <p className="text-lg text-slate-600">Processing authentication...</p>
    </div>
  </div>;
}

// SLTGameBridge - Global bridge for interactive HTML polls
// Requires login to vote - session_token cookie used for authentication
function useSLTGameBridge(user, login) {
  useEffect(() => {
    // Create the bridge object
    const bridge = {
      /**
       * Get poll state including authentication status and vote counts
       * @param {string} pollId - The poll identifier
       * @returns {Promise<{authenticated: boolean, voted: boolean, counts: number[], selectedIndex?: number}>}
       */
      getPoll: async (pollId) => {
        try {
          const response = await axios.get(`${API}/polls/game/${pollId}`, {
            withCredentials: true
          });
          return {
            authenticated: response.data.authenticated,
            voted: response.data.voted,
            counts: response.data.counts,
            selectedIndex: response.data.selected_index,
            totalVotes: response.data.total_votes,
            options: response.data.options,
            question: response.data.question
          };
        } catch (error) {
          console.error('Error fetching poll:', error);
          return {
            authenticated: false,
            voted: false,
            counts: [],
            error: error.response?.data?.detail || 'Failed to load poll'
          };
        }
      },
      
      /**
       * Submit a vote for a poll - REQUIRES LOGIN
       * @param {string} pollId - The poll identifier
       * @param {number} optionIndex - The index of the selected option (0-based)
       * @returns {Promise<{success: boolean, counts: number[], selectedIndex: number}>}
       */
      vote: async (pollId, optionIndex) => {
        try {
          const response = await axios.post(
            `${API}/polls/game/${pollId}/vote`,
            { option_index: optionIndex },
            { withCredentials: true }
          );
          return {
            success: true,
            authenticated: true,
            voted: true,
            counts: response.data.counts,
            selectedIndex: response.data.selected_index,
            totalVotes: response.data.total_votes
          };
        } catch (error) {
          console.error('Error voting:', error);
          const detail = error.response?.data?.detail || 'Failed to submit vote';
          
          if (error.response?.status === 401) {
            return {
              success: false,
              requiresAuth: true,
              error: 'Please sign in to vote'
            };
          }
          
          if (detail.includes('already voted') || detail.includes('Already voted')) {
            return {
              success: false,
              alreadyVoted: true,
              error: 'You have already voted on this poll'
            };
          }
          
          return {
            success: false,
            error: detail
          };
        }
      },
      
      /**
       * Open the login flow and return user to current page after authentication
       * Optionally stores a pending vote to be submitted after login
       */
      login: (pendingPollId = null, pendingOptionIndex = null) => {
        const returnUrl = window.location.pathname + window.location.search;
        // Use localStorage instead of sessionStorage to persist across auth redirect
        localStorage.setItem('authReturnUrl', returnUrl);
        
        // Store pending vote if provided
        if (pendingPollId !== null && pendingOptionIndex !== null) {
          localStorage.setItem('pendingVote', JSON.stringify({
            pollId: pendingPollId,
            optionIndex: pendingOptionIndex
          }));
        }
        
        login();
      },
      
      /**
       * Check if user is currently authenticated
       */
      isAuthenticated: () => {
        return !!user;
      },
      
      /**
       * Get current user info (if authenticated)
       */
      getUser: () => {
        if (!user) return null;
        return {
          name: user.name,
          picture: user.picture
        };
      }
    };
    
    // Expose bridge on window object
    window.SLTGameBridge = bridge;
    
    // Also expose as SheLovesTheePolls for backward compatibility with Women's Media Day article.
    // That article's embedded HTML identifies options by string slug (e.g. 'aamu', 'su'), not by
    // numeric index, and expects get()/vote() to speak in slugs: counts keyed by slug, myVote as a
    // slug, and a thrown error with response.status on failure (axios-style) rather than a
    // {success:false} return. The underlying /api/polls/game backend only knows numeric
    // option_index, so this maps slug<->index per poll using each poll's option order, which must
    // stay in the same order as its definition in game_polls.py GAME_POLLS.
    const SLT_POLL_OPTION_SLUGS = {
      'slt-wbb-2026-27-title': ['su', 'aamu', 'asu', 'alcorn', 'uapb', 'gsu', 'famu', 'jsu', 'bcu', 'tsu', 'mvsu', 'pv'],
      'slt-wbb-2026-27-awards': ['bowen', 'tate', 'aamu', 'famu'],
      'slt-wbb-2026-27-honors': ['su', 'aamu', 'asu', 'uapb', 'famu'],
      'slt-wbb-2026-27-surprise': ['famu', 'jsu', 'uapb', 'top'],
      'slt-wbb-2026-27-climber': ['famu', 'jsu', 'bcu', 'tsu', 'mvsu', 'pv'],
      'slt-wbb-2026-27-debate': ['defense', 'guards', 'depth', 'coaching']
    };

    window.SheLovesTheePolls = {
      get: async (pollId) => {
        const result = await bridge.getPoll(pollId);
        const slugs = SLT_POLL_OPTION_SLUGS[pollId];
        const counts = {};
        (result.counts || []).forEach((c, i) => {
          const key = slugs ? slugs[i] : i;
          if (key !== undefined) counts[key] = c;
        });
        return {
          ...result,
          counts,
          myVote: slugs && result.selectedIndex != null ? slugs[result.selectedIndex] : result.selectedIndex,
          // Alias for compatibility
          userVote: result.selectedIndex,
          percentages: (result.counts || []).map((c) =>
            result.totalVotes > 0 ? Math.round((c / result.totalVotes) * 100) : 0
          )
        };
      },
      vote: async (pollId, optionIdOrIndex) => {
        const slugs = SLT_POLL_OPTION_SLUGS[pollId];
        const optionIndex = slugs ? slugs.indexOf(optionIdOrIndex) : optionIdOrIndex;
        const result = await bridge.vote(pollId, optionIndex === -1 ? optionIdOrIndex : optionIndex);
        if (result.requiresAuth) {
          const err = new Error(result.error || 'Authentication required');
          err.response = { status: 401 };
          throw err;
        }
        return {
          ...result,
          myVote: slugs ? optionIdOrIndex : result.selectedIndex
        };
      },
      isLoggedIn: () => bridge.isAuthenticated(),
      login: () => bridge.login(),
      getUser: () => bridge.getUser()
    };
    
    // Community bridge: anonymous votes + login-required, moderated comments (Hot Seat article)
    const getBrowserId = getVisitorId;
    window.SHELOVESTHEE_COMMUNITY = {
      getCurrentUser: async () => {
        try {
          const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
          const u = res.data;
          return u ? { id: u.user_id, displayName: u.nickname || u.name || 'SheLovesThee Fan' } : null;
        } catch (e) {
          return null;
        }
      },
      getVoteTotals: async ({ articleId, pollId }) =>
        (await axios.get(`${API}/community/votes`, { params: { articleId, pollId } })).data,
      submitVote: async ({ articleId, pollId, value }) =>
        (await axios.post(`${API}/community/votes`, { articleId, pollId, value, browserId: getBrowserId() })).data,
      getComments: async ({ articleId }) =>
        (await axios.get(`${API}/community/comments`, { params: { articleId } })).data,
      postComment: async ({ articleId, body }) => {
        const data = (await axios.post(`${API}/community/comments`, { articleId, body }, { withCredentials: true })).data;
        if (data?.status === 'pending') {
          setTimeout(() => {
            const el = document.getElementById('comment-status');
            if (el) el.textContent = 'Thanks! Your comment was submitted and will appear after it is approved.';
          }, 50);
        }
        return data;
      },
      login: () => {
        localStorage.setItem('authReturnUrl', window.location.pathname + window.location.search + '#thee-comments');
        login();
      }
    };

    // Dispatch ready event to notify HTML blocks that the bridge is available
    console.log('[SLTGameBridge] Bridge initialized, dispatching ready event');
    window.dispatchEvent(new Event('slt-game-bridge-ready'));
    // Also dispatch shelovesthee-polls-ready for backward compatibility
    window.dispatchEvent(new Event('shelovesthee-polls-ready'));
    
    // Check for pending vote after login
    const processPendingVote = async () => {
      const pendingVoteStr = localStorage.getItem('pendingVote');
      if (pendingVoteStr && user) {
        localStorage.removeItem('pendingVote');
        try {
          const pendingVote = JSON.parse(pendingVoteStr);
          console.log('[SLTGameBridge] Processing pending vote:', pendingVote);
          const result = await bridge.vote(pendingVote.pollId, pendingVote.optionIndex);
          if (result.success) {
            console.log('[SLTGameBridge] Pending vote submitted successfully');
            // Dispatch event to notify article to refresh poll state
            window.dispatchEvent(new CustomEvent('slt-vote-completed', { 
              detail: { pollId: pendingVote.pollId, result } 
            }));
          }
        } catch (e) {
          console.error('[SLTGameBridge] Failed to submit pending vote:', e);
        }
      }
    };
    
    // Process pending vote after a short delay to ensure everything is loaded
    if (user) {
      setTimeout(processPendingVote, 500);
    }
    
    // Also dispatch after delays for scripts that load later
    const timeouts = [
      setTimeout(() => {
        window.dispatchEvent(new Event('slt-game-bridge-ready'));
        window.dispatchEvent(new Event('shelovesthee-polls-ready'));
      }, 500),
      setTimeout(() => {
        window.dispatchEvent(new Event('slt-game-bridge-ready'));
        window.dispatchEvent(new Event('shelovesthee-polls-ready'));
      }, 1500),
      setTimeout(() => {
        window.dispatchEvent(new Event('slt-game-bridge-ready'));
        window.dispatchEvent(new Event('shelovesthee-polls-ready'));
      }, 3000)
    ];
    
    // NO cleanup - keep bridge available globally
    return () => {
      timeouts.forEach(t => clearTimeout(t));
      // Don't delete the bridge - it should persist
    };
  }, [user, login]);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    // Skip auth check if we're processing a session_id in the hash
    if (window.location.hash?.includes('session_id=')) {
      console.log('checkAuth: Skipping - hash contains session_id');
      setLoading(false);
      return;
    }

    console.log('checkAuth: Checking authentication...');
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      console.log('checkAuth: Got user data:', response.data);
      setUser(response.data);
      
      // Update localStorage backup
      localStorage.setItem('slt_user', JSON.stringify(response.data));
      
      // Check if user needs to set nickname (only for logged-in users without nickname)
      if (response.data && !response.data.nickname) {
        console.log('checkAuth: User needs to set nickname');
        setShowNicknameModal(true);
      }
    } catch (error) {
      console.log('checkAuth: Not authenticated via cookie', error.response?.status);
      
      // Try to recover from localStorage
      const savedUser = localStorage.getItem('slt_user');
      if (savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          console.log('checkAuth: Found saved user in localStorage, re-verifying...');
          // Clear it - will need fresh login
          localStorage.removeItem('slt_user');
        } catch (e) {
          // Ignore parse errors
        }
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (location.state?.user) {
      const userData = location.state.user;
      console.log('AuthProvider: Setting user from location state:', userData);
      setUser(userData);
      
      // Check if newly logged-in user needs nickname
      if (userData && !userData.nickname) {
        console.log('AuthProvider: User needs to set nickname (from location state)');
        setShowNicknameModal(true);
      }
    }
  }, [location]);

  const login = () => {
    // Store current URL for return after auth (if not already stored by bridge)
    // Use localStorage to persist across the external auth redirect
    if (!localStorage.getItem('authReturnUrl')) {
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== '/' && currentPath !== '/login') {
        localStorage.setItem('authReturnUrl', currentPath);
      }
    }
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Initialize the global SLTGameBridge for interactive polls
  useSLTGameBridge(user, login);

  // Intercept the Hot Seat article's plain "/login" link so users return to the comments section
  useEffect(() => {
    const onClick = (e) => {
      const link = e.target.closest?.('#comment-login');
      if (!link || !window.SHELOVESTHEE_COMMUNITY) return;
      e.preventDefault();
      window.SHELOVESTHEE_COMMUNITY.login();
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  });

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setShowNicknameModal(false);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNicknameSet = (updatedUser) => {
    setUser(updatedUser);
    setShowNicknameModal(false);
  };

  // Function to check if user needs nickname (for components to call)
  const requiresNickname = () => {
    return user && !user.nickname;
  };

  // Function to prompt nickname setup (works for both new and existing nicknames)
  const promptNickname = () => {
    if (user) {
      setShowNicknameModal(true);
      return true;
    }
    return false;
  };

  // Close nickname modal
  const closeNicknameModal = () => {
    setShowNicknameModal(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser, 
      loading, 
      login, 
      logout, 
      checkAuth,
      requiresNickname,
      promptNickname,
      showNicknameModal 
    }}>
      {children}
      <NicknameModal 
        isOpen={showNicknameModal} 
        onClose={closeNicknameModal}
        onNicknameSet={handleNicknameSet}
        user={user}
      />
    </AuthContext.Provider>
  );
}

function AppRouter() {
  const location = useLocation();
  const [hasSessionId, setHasSessionId] = React.useState(false);

  React.useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  React.useEffect(() => {
    const dedicatedArticlePaths = new Set([
      '/jackson-state-soccer',
      '/boombox-classic',
      '/jsu-basketball-2026-27',
      '/jsu-vs-aamu-2026',
      '/swac-tv',
      '/how-to-watch-swac',
    ]);
    const isArticleRoute = location.pathname.startsWith('/news/')
      || location.pathname.startsWith('/interactive/')
      || dedicatedArticlePaths.has(location.pathname);

    // A deliberate in-article hash (such as a post-login return to comments)
    // remains respected; all normal article links begin at the headline.
    if (!isArticleRoute || location.hash) return undefined;

    window.scrollTo(0, 0);
    const frame = window.requestAnimationFrame(() => window.scrollTo(0, 0));
    const settleTimer = window.setTimeout(() => window.scrollTo(0, 0), 600);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
    };
  }, [location.pathname, location.hash]);
  
  // Check for session_id in the URL hash on mount and location changes
  React.useEffect(() => {
    const checkHash = () => {
      const hash = window.location.hash;
      setHasSessionId(hash?.includes('session_id='));
    };
    
    checkHash();
    
    // Listen for hash changes
    window.addEventListener('hashchange', checkHash);
    return () => window.removeEventListener('hashchange', checkHash);
  }, [location]);
  
  if (hasSessionId) {
    return <AuthCallback />;
  }

  return (
    <div className="App">
      <Navbar />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/:id" element={<ArticlePage />} />
        <Route path="/fan-zone" element={<FanZonePage />} />
        <Route path="/polls" element={<PollsPage />} />
        <Route path="/qb-club" element={<QBClubPage />} />
        <Route path="/qb-analytics" element={<QBAnalyticsPage />} />
        <Route path="/jackson-state" element={<JacksonStatePage />} />
        <Route path="/swac-pickem" element={<SWACPickemPage />} />
        <Route path="/pick-history" element={<PickHistoryPage />} />
        <Route path="/admin/pickem" element={<AdminPickemPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/members" element={<MembersPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/jackson-state-soccer" element={<JacksonStateSoccerPage />} />
        <Route path="/boombox-classic" element={<BoomBoxClassicPage />} />
        <Route path="/swac-scoreboard" element={<SWACScoreboardPage />} />
        <Route path="/gameday" element={<GamedayScoreboardPage />} />
        <Route path="/jsu-basketball-2026-27" element={<JSUBasketballArticlePage />} />
        <Route path="/jsu-vs-aamu-2026" element={<JSUvsAAMUArticlePage />} />
        <Route path="/swac-tv" element={<SWACTVArticlePage />} />
        <Route path="/how-to-watch-swac" element={<SWACTVArticlePage />} />
        <Route path="/interactive/:id" element={<InteractiveArticleViewer />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      {/* Default SEO tags - will be overridden by page-specific Helmet tags */}
      <Helmet>
        <title>SheLovesThee Podcast | HBCU Sports News, SWAC Football & Jackson State Coverage</title>
        <meta name="description" content="SheLovesThee Podcast - Your premier source for HBCU sports news, SWAC football coverage, Jackson State Tigers updates, and more." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="SheLovesThee - HBCU Sports & SWAC Football" />
        <meta property="og:description" content="Your premier source for HBCU sports news, SWAC football coverage, and Jackson State Tigers updates." />
        <meta property="og:url" content="https://shelovesthee.com" />
        <meta property="og:image" content="https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/12mdvn7w_9ABE93F3-1197-42CC-98EE-6245B2328755.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SheLovesThee - HBCU Sports & SWAC Football" />
        <meta name="twitter:description" content="Your premier source for HBCU sports news, SWAC football coverage, and Jackson State Tigers updates." />
        <meta name="twitter:image" content="https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/12mdvn7w_9ABE93F3-1197-42CC-98EE-6245B2328755.png" />
      </Helmet>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;